
/**
 * TiledWindowContext - Global state management for tiled windows
 * 
 * Provides context for opening, closing, focusing, and managing tiled windows.
 * Windows can display any entity type (workflow, agent, tool, etc.) and support
 * recursive drill-down navigation.
 */

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import type {
    TiledWindow,
    TiledWindowContextValue,
    TiledWindowEntityType,
    OpenWindowRequest,
    WindowGeometry,
    WindowState,
    WorkflowWindow,
    AgentWindow,
    ToolWindow,
    PromptWindow,
    ModelWindow,
} from '@/types/tiled-window';
import type { AvailableResources } from '@/types/agent-graph';
import { extractDefinitionFromEntity } from '@/components/workflow-graph/utils/extract-workflow-definition';

// Re-export for convenience
export type { OpenWindowRequest } from '@/types/tiled-window';

// Default sizes imported from types
const defaultSizes: Record<TiledWindowEntityType, { width: number; height: number }> = {
    workflow: { width: 900, height: 650 },
    agent: { width: 850, height: 600 },
    tool: { width: 600, height: 500 },
    prompt: { width: 700, height: 550 },
    model: { width: 800, height: 550 },  // Larger for ModelGraph visualization
    skill: { width: 600, height: 450 },
    role: { width: 600, height: 450 },
    policy: { width: 700, height: 550 },
    schema: { width: 700, height: 550 },
    decision: { width: 850, height: 650 },
};

/**
 * Fetch available resources (models, prompts, tools, skills) for an app.
 * Used to populate AvailableResources when opening agent/workflow tiled windows,
 * so that model/tool/prompt references can be resolved (avoiding "not found").
 */
async function fetchAppResources(appKey: string) {
    type AppCapabilityResource = {
        id?: number;
        name: string;
        aliases?: string[];
        description?: string;
        app_key?: string;
        fqcn?: string;
    };

    type FetchedAppResources = AvailableResources & {
        agents: AppCapabilityResource[];
    };

    const fetchType = async (type: string) => {
        try {
            const res = await fetch(
                `/api/applications/${encodeURIComponent(appKey)}/capabilities?type=${type}&per_page=200`,
                { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' },
            );
            if (!res.ok) return [];
            const json = await res.json();
            return json.data || [];
        } catch {
            return [];
        }
    };

    const [models, prompts, tools, skills, roles, agents, workflows, memories] = await Promise.all([
        fetchType('model'),
        fetchType('prompt'),
        fetchType('tool'),
        fetchType('skill'),
        fetchType('role'),
        fetchType('agent'),
        fetchType('workflow'),
        fetchType('memory'),
    ]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toResource = (cap: any) => {
        const fqcn = cap.fqcn ?? (cap.app_key && cap.name ? `${cap.app_key}/${cap.name}` : undefined);
        const aliases = [...(cap.aliases ?? [])];
        if (fqcn && !aliases.includes(fqcn)) aliases.push(fqcn);
        return {
            id: Number(cap.id) || undefined,
            name: cap.name,
            aliases: aliases.length > 0 ? aliases : undefined,
            description: cap.description ?? undefined,
            app_key: cap.app_key,
            fqcn,
        };
    };

    const resources: FetchedAppResources = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        models: models.map((m: any) => ({ ...toResource(m), provider: m.provider })),
        prompts: prompts.map(toResource),
        tools: tools.map(toResource),
        skills: skills.map(toResource),
        roles: roles.map(toResource),
        agents: agents.map(toResource),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        workflows: workflows.map((w: any) => ({ ...toResource(w), id: w.id != null ? String(w.id) : undefined, key: w.name })),
        memories: memories.map(toResource),
    };

    return resources;
}

/**
 * Map a capability API response to the viewer-expected data structure.
 *
 * The capabilities API returns a unified object with `working_revision.type_config`
 * containing the type-specific fields. Each viewer expects a type-keyed wrapper
 * (e.g. `{ model: ModelWithBackup }` for the model viewer).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapCapabilityToWindowData(entityType: TiledWindowEntityType, cap: any): Partial<TiledWindow['data']> {
    const revision = cap.working_revision || cap.immutable_revisions?.[0];
    const typeConfig = revision?.type_config || cap.config || {};
    const version = revision?.version_string || '1.0.0';
    const methods = cap.methods || revision?.methods || [];

    switch (entityType) {
        case 'model':
            return {
                model: {
                    id: cap.id,
                    name: cap.fqcn || cap.name,
                    provider: typeConfig.provider || '',
                    model: typeConfig.model || '',
                    parameters: typeConfig.parameters || {},
                    retry: typeConfig.retry || null,
                    backup: typeConfig.backup || null,
                    description: cap.description,
                    version,
                    is_active: cap.status === 'active',
                    tags: cap.tags || [],
                    created: cap.created,
                    modified: cap.modified,
                },
            };
        case 'tool':
            return {
                tool: {
                    id: cap.id,
                    name: cap.fqcn || cap.name,
                    version,
                    source: null,
                    priority: 0,
                    description: cap.description,
                    handler_class: typeConfig.handler_class || null,
                    input_schema: typeConfig.input_schema || methods[0]?.input_schema || null,
                    output_schema: typeConfig.output_schema || methods[0]?.output_schema || null,
                    metadata: typeConfig.metadata || {},
                    tags: cap.tags || [],
                    is_active: cap.status === 'active',
                    content_hash: cap.content_hash,
                    created: cap.created,
                    modified: cap.modified,
                },
            };
        case 'prompt':
            return {
                prompt: {
                    id: cap.id,
                    name: cap.fqcn || cap.name,
                    version,
                    content: typeConfig.content || typeConfig.template || '',
                    metadata: typeConfig.metadata || null,
                    type: typeConfig.type || cap.type || null,
                    category: typeConfig.category || cap.domain || null,
                    description: cap.description,
                    tags: cap.tags || [],
                    is_active: cap.status === 'active',
                    created: cap.created,
                    modified: cap.modified,
                },
            };
        case 'agent':
            return {
                agent: {
                    id: String(cap.id),
                    name: cap.fqcn || cap.name,
                    slug: cap.name,
                    type: typeConfig.type || 'agent',
                    version,
                    source: null,
                    priority: 0,
                    description: cap.description,
                    handler_class: typeConfig.handler_class || null,
                    model: typeConfig.model || null,
                    pre_processor_workflow: typeConfig.pre_processor_workflow || null,
                    post_processor_workflow: typeConfig.post_processor_workflow || null,
                    prompt: typeConfig.prompt || null,
                    tools: typeConfig.tools || [],
                    skills: typeConfig.skills || [],
                    roles: typeConfig.roles || [],
                    memory: typeConfig.memory || [],
                    prompts: typeConfig.prompts || [],
                    capabilities: typeConfig.capabilities || null,
                    metadata: typeConfig.metadata || {},
                    tags: cap.tags || [],
                    visibility: cap.visibility || 'account',
                    is_active: cap.status === 'active',
                    content_hash: cap.content_hash,
                    created: cap.created,
                    modified: cap.modified,
                },
                resources: { models: [], prompts: [], workflows: [], tools: [], skills: [], roles: [], memories: [] },
            };
        case 'workflow': {
            const definition = extractDefinitionFromEntity(cap);
            const fqcn = cap.fqcn || (cap.app_key ? `${cap.app_key}/${cap.name}` : cap.name);
            return {
                workflow: {
                    id: String(cap.id),
                    team_id: null,
                    key: fqcn,
                    name: fqcn || cap.name,
                    description: cap.description,
                    format: typeConfig.format || 'asl',
                    definition,
                    tags: cap.tags || [],
                    status: cap.status,
                    created: cap.created,
                    modified: cap.modified,
                    has_runs: Boolean(cap.has_runs),
                },
                definition,
                availableWorkflows: [],
                agents: [],
                tools: [],
                models: [],
            };
        }
        case 'skill':
            return {
                skill: {
                    id: cap.id,
                    app_id: cap.app_key,
                    domain: cap.domain,
                    name: cap.fqcn || cap.name,
                    version,
                    type: typeConfig.type || 'skill',
                    description: cap.description,
                    definition: typeConfig.definition || typeConfig,
                    metadata: typeConfig.metadata || {},
                    tags: cap.tags || [],
                    is_active: cap.status === 'active',
                    visibility: cap.visibility || 'account',
                    content_hash: cap.content_hash,
                    created: cap.created,
                    modified: cap.modified,
                },
            };
        default:
            return cap;
    }
}

const TiledWindowContext = createContext<TiledWindowContextValue | null>(null);

/** Generate unique window ID */
function generateWindowId(): string {
    return `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

interface TiledWindowProviderProps {
    children: ReactNode;
}

export function TiledWindowProvider({ children }: TiledWindowProviderProps) {
    const [windows, setWindows] = useState<TiledWindow[]>([]);
    const [focusedWindowId, setFocusedWindowId] = useState<string | null>(null);
    const nextZIndexRef = useRef(1);

    /**
     * Fetch entity data from the capabilities API.
     *
     * Uses the unified `/api/applications/{appKey}/capabilities/{name}` endpoint.
     * Falls back to `/api/capabilities/{id}` when no appKey is available.
     *
     * When entityId is a numeric DB id (not a capability UUID), entityName is
     * used for the name-based lookup instead.
     */
    const fetchEntityData = useCallback(async (
        entityType: TiledWindowEntityType,
        entityId: string | number,
        appKey?: string,
        entityName?: string
    ): Promise<{ data: Partial<TiledWindow['data']>; resolvedAppKey?: string }> => {
        // Prefer the string name for capability lookups; numeric IDs are from a
        // legacy table and don't work with the capabilities API.
        const isNumericId = typeof entityId === 'number' || /^\d+$/.test(String(entityId));
        const lookupName = isNumericId && entityName ? entityName : String(entityId);

        let endpoint: string;

        if (lookupName.includes('/')) {
            // Slash-separated format: "appKey/capName" (optionally with "::method" suffix)
            const cleanId = lookupName.replace(/::.*$/, '');
            const slashIdx = cleanId.indexOf('/');
            const parsedAppKey = cleanId.substring(0, slashIdx);
            const capName = cleanId.substring(slashIdx + 1);
            endpoint = `/api/applications/${encodeURIComponent(parsedAppKey)}/capabilities/${encodeURIComponent(capName)}`;
        } else if (appKey) {
            endpoint = `/api/applications/${encodeURIComponent(appKey)}/capabilities/${encodeURIComponent(lookupName)}`;
        } else {
            endpoint = `/api/capabilities/${encodeURIComponent(lookupName)}`;
        }

        console.log(`[TiledWindow] Fetching ${entityType}:`, { entityId, entityName, appKey, lookupName, endpoint });

        try {
            const response = await fetch(endpoint, {
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
            });

            console.log(`[TiledWindow] Response for ${entityType}:`, {
                status: response.status,
                statusText: response.statusText,
                ok: response.ok
            });

            if (!response.ok) {
                const errorBody = await response.text();
                console.error(`[TiledWindow] Error response body:`, errorBody);
                throw new Error(`Failed to fetch ${entityType}: ${response.status} ${response.statusText}`);
            }

            const capData = await response.json();
            console.log(`[TiledWindow] Received capability for ${entityType}:`, {
                id: capData.id,
                name: capData.name,
                fqcn: capData.fqcn,
                type: capData.type,
            });

            const windowData = mapCapabilityToWindowData(entityType, capData);

            // For agent and workflow windows, fetch the app's capabilities so
            // nested graphs can resolve model/tool/prompt/skill references.
            const resolvedAppKey = appKey || capData.app_key;
            if ((entityType === 'agent' || entityType === 'workflow') && resolvedAppKey) {
                try {
                    // Collect all app keys we need to fetch from: the entity's own
                    // app plus any foreign apps referenced via "appKey/capName" format
                    const typeConfigRefs = capData.working_revision?.type_config || capData.config || {};
                    const allRefs: string[] = [
                        ...(typeConfigRefs.tools || []),
                        ...(typeConfigRefs.skills || []),
                        typeConfigRefs.model,
                        typeConfigRefs.prompt,
                    ].filter(Boolean);

                    const foreignAppKeys = new Set<string>();
                    for (const ref of allRefs) {
                        const cleanRef = String(ref).replace(/::.*$/, '');
                        if (cleanRef.includes('/')) {
                            const refAppKey = cleanRef.substring(0, cleanRef.indexOf('/'));
                            if (refAppKey && refAppKey !== resolvedAppKey) {
                                foreignAppKeys.add(refAppKey);
                            }
                        }
                    }

                    // Fetch from the entity's app and any foreign apps in parallel
                    const appKeysToFetch = [resolvedAppKey, ...foreignAppKeys];
                    const allResources = await Promise.all(
                        appKeysToFetch.map(ak => fetchAppResources(ak))
                    );

                    // Merge all resources (primary app first, then foreign apps)
                    const resources = allResources[0];
                    for (let i = 1; i < allResources.length; i++) {
                        const foreign = allResources[i];
                        resources.models.push(...foreign.models);
                        resources.prompts.push(...foreign.prompts);
                        resources.tools.push(...foreign.tools);
                        resources.skills.push(...foreign.skills);
                        resources.roles.push(...foreign.roles);
                        resources.agents.push(...foreign.agents);
                        resources.workflows.push(...foreign.workflows);
                    }

                    console.log(`[TiledWindow] Fetched resources for ${entityType} from ${appKeysToFetch.length} app(s) [${appKeysToFetch.join(', ')}]:`, {
                        models: resources.models.length,
                        prompts: resources.prompts.length,
                        tools: resources.tools.length,
                        skills: resources.skills.length,
                        roles: resources.roles.length,
                        agents: resources.agents.length,
                        workflows: resources.workflows.length,
                    });

                    if (entityType === 'agent') {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (windowData as any).resources = {
                            models: resources.models,
                            prompts: resources.prompts,
                            workflows: resources.workflows,
                            tools: resources.tools,
                            skills: resources.skills,
                            roles: resources.roles,
                        };
                    } else if (entityType === 'workflow') {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const wd = windowData as any;
                        wd.agents = resources.agents.map((agent) => ({
                            name: agent.name,
                            fqcn: agent.fqcn ?? agent.name,
                            description: agent.description,
                        }));
                        wd.tools = resources.tools.map((tool) => ({
                            name: tool.name,
                            fqcn: tool.fqcn ?? tool.name,
                            description: tool.description,
                            app_key: tool.app_key,
                        }));
                        wd.models = resources.models.map((model) => ({
                            name: model.name,
                            fqcn: model.fqcn ?? model.name,
                            description: model.description,
                        }));
                        wd.availableWorkflows = resources.workflows.map((w) => ({
                            id: w.id ?? w.key,
                            key: w.key,
                            name: w.name,
                            definition: {},
                        }));
                    }
                } catch (err) {
                    console.warn(`[TiledWindow] fetchAppResources: Failed to fetch resources for ${entityType}, references may show as unresolved:`, err);
                }
            }

            return { data: windowData, resolvedAppKey };
        } catch (error) {
            console.error(`[TiledWindow] Failed to fetch ${entityType} ${entityId}:`, error);
            throw error;
        }
    }, []);

    /**
     * Open a new tiled window
     * 
     * Windows always open at position 0,0. Child windows inherit their parent's
     * dimensions (max-w, max-h of parent).
     */
    const openWindow = useCallback(async (request: OpenWindowRequest): Promise<string> => {
        const { entityType, entityId, entityName, appKey, parentWindowId, position, size, replayRunId } = request;

        // Check if window for this entity already exists
        const existingWindow = windows.find(
            w => w.entityType === entityType && w.entityId === entityId
        );
        if (existingWindow) {
            // Focus existing window and update replay context if provided
            setFocusedWindowId(existingWindow.id);
            setWindows(prev => prev.map(w => {
                if (w.id !== existingWindow.id) return w;
                
                // Update zIndex and also update replayRunId if provided
                const updated = { ...w, zIndex: nextZIndexRef.current++ };
                
                // For workflow windows, update replayRunId if it changed
                if (entityType === 'workflow' && w.entityType === 'workflow') {
                    const workflowWindow = updated as WorkflowWindow;
                    if (replayRunId && workflowWindow.data?.replayRunId !== replayRunId) {
                        console.log('[TiledWindow] Updating existing window with new replayRunId:', replayRunId);
                        workflowWindow.data = { ...workflowWindow.data, replayRunId };
                    }
                }
                
                return updated;
            }));
            return existingWindow.id;
        }

        const windowId = generateWindowId();
        
        // Determine window size:
        // 1. If explicit size provided, use it
        // 2. If has parent, inherit parent's dimensions (child fills parent)
        // 3. Otherwise use default size for entity type
        let windowSize: { width: number; height: number };
        if (size) {
            windowSize = size;
        } else if (parentWindowId) {
            const parentWindow = windows.find(w => w.id === parentWindowId);
            if (parentWindow) {
                // Child window fills parent's dimensions
                windowSize = {
                    width: parentWindow.geometry.width,
                    height: parentWindow.geometry.height,
                };
            } else {
                windowSize = defaultSizes[entityType];
            }
        } else {
            windowSize = defaultSizes[entityType];
        }
        
        // Always open at 0,0
        const pos = position || { x: 0, y: 0 };

        // Create placeholder window (loading state)
        // Windows always open maximized to fill their container
        const placeholderWindow: TiledWindow = {
            id: windowId,
            title: entityName,
            entityType,
            entityId,
            entityName,
            appKey,
            geometry: {
                x: pos.x,
                y: pos.y,
                width: windowSize.width,
                height: windowSize.height,
            },
            state: 'maximized',
            zIndex: nextZIndexRef.current++,
            isLoading: true,
            parentWindowId,
            data: {} as never, // Placeholder, will be populated after fetch
        } as TiledWindow;

        setWindows(prev => [...prev, placeholderWindow]);
        setFocusedWindowId(windowId);

        try {
            const { data: entityData, resolvedAppKey } = await fetchEntityData(entityType, entityId, appKey, entityName);

            setWindows(prev => prev.map(w => {
                if (w.id !== windowId) return w;

                return {
                    ...w,
                    isLoading: false,
                    appKey: resolvedAppKey || w.appKey,
                    data: entityType === 'workflow' && replayRunId
                        ? { ...entityData, replayRunId }
                        : entityData,
                } as TiledWindow;
            }));
        } catch (error) {
            // Update window with error state
            setWindows(prev => prev.map(w => {
                if (w.id !== windowId) return w;

                return {
                    ...w,
                    isLoading: false,
                    error: error instanceof Error ? error.message : 'Failed to load data',
                };
            }));
        }

        return windowId;
    }, [windows, fetchEntityData]);

    /**
     * Close a window by ID
     */
    const closeWindow = useCallback((id: string) => {
        setWindows(prev => prev.filter(w => w.id !== id));
        
        // If closing focused window, focus next available
        setFocusedWindowId(prev => {
            if (prev !== id) return prev;
            const remaining = windows.filter(w => w.id !== id);
            if (remaining.length === 0) return null;
            // Focus the window with highest zIndex
            return remaining.reduce((a, b) => a.zIndex > b.zIndex ? a : b).id;
        });
    }, [windows]);

    /**
     * Close all windows
     */
    const closeAllWindows = useCallback(() => {
        setWindows([]);
        setFocusedWindowId(null);
        nextZIndexRef.current = 1;
    }, []);

    /**
     * Focus a window (bring to front)
     */
    const focusWindow = useCallback((id: string) => {
        setFocusedWindowId(id);
        setWindows(prev => prev.map(w => 
            w.id === id 
                ? { ...w, zIndex: nextZIndexRef.current++ }
                : w
        ));
    }, []);

    /**
     * Update window geometry (position/size)
     */
    const updateGeometry = useCallback((id: string, geometry: Partial<WindowGeometry>) => {
        setWindows(prev => prev.map(w => 
            w.id === id 
                ? { ...w, geometry: { ...w.geometry, ...geometry } }
                : w
        ));
    }, []);

    /**
     * Update window state (normal/minimized/maximized)
     */
    const updateState = useCallback((id: string, state: WindowState) => {
        setWindows(prev => prev.map(w => 
            w.id === id ? { ...w, state } : w
        ));
    }, []);

    /**
     * Get a window by ID
     */
    const getWindow = useCallback((id: string): TiledWindow | undefined => {
        return windows.find(w => w.id === id);
    }, [windows]);

    /**
     * Find window by entity type and ID
     */
    const findWindowByEntity = useCallback((
        entityType: TiledWindowEntityType,
        entityId: string | number
    ): TiledWindow | undefined => {
        return windows.find(w => w.entityType === entityType && w.entityId === entityId);
    }, [windows]);

    const contextValue: TiledWindowContextValue = {
        windows,
        focusedWindowId,
        openWindow,
        closeWindow,
        closeAllWindows,
        focusWindow,
        updateGeometry,
        updateState,
        getWindow,
        findWindowByEntity,
    };

    return (
        <TiledWindowContext.Provider value={contextValue}>
            {children}
        </TiledWindowContext.Provider>
    );
}

/**
 * Hook to access tiled window context
 */
export function useTiledWindows(): TiledWindowContextValue {
    const context = useContext(TiledWindowContext);
    if (!context) {
        throw new Error('useTiledWindows must be used within a TiledWindowProvider');
    }
    return context;
}

/**
 * Hook to check if context is available (for conditional usage)
 */
export function useTiledWindowsOptional(): TiledWindowContextValue | null {
    return useContext(TiledWindowContext);
}

